import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useStore } from 'app/context';
import { LeadNowAbosOpts, LeadNowNettoOpt, LeadNowVM } from 'app/stores/ui/agt.leadnow.ui.store';
import { SelectInput, ISelectOpt } from 'app/components/common/Select';
import { runInAction } from 'mobx';
import colors from 'assets/scss/colors.module.scss';

import { APIProvider, Map, useAdvancedMarkerRef, AdvancedMarker, AdvancedMarkerAnchorPoint } from '@vis.gl/react-google-maps';
import { Button } from 'app/components/common/Button';
import { Icon } from 'app/components/common/Icon';
import { LeadNowInfoWindow } from 'app/components/maps/InfoWindow';
import { SimpleMarker as ReusableSimpleMarker, FeatureMarker as ReusableFeatureMarker, ClusterFeatureMarker as ReusableClusterFeatureMarker } from 'app/components/maps/Markers';
import { ClusterProperties } from 'supercluster';
import { Feature, Point } from 'geojson';
import { useSupercluster } from 'app/utils/maps';

interface IInfoWindowData {
	anchor: google.maps.marker.AdvancedMarkerElement;
	item: LeadNowVM;
}

export const LeadNowVerteilungGoogleMap = observer(() => {
	const { berichteUiiStore, agtLeadNowUiStore, agenturListUiStore } = useStore();
	const [items, setItems] = useState<LeadNowVM[]>();
	const [empties, setEmpties] = useState<LeadNowVM[]>();
	const [abo, setAbo] = useState<ISelectOpt>(LeadNowNettoOpt);
	const [showEmpties, setShowEmpties] = useState(false);
	const [infoWindowData, setInfoWindowData] = useState<IInfoWindowData | null>(null);

	useEffect(() => {
		agtLeadNowUiStore.load();
	}, [agtLeadNowUiStore]);

	useEffect(() => {
		agtLeadNowUiStore.aggregateAgtsByBm(agtLeadNowUiStore.minKw, agtLeadNowUiStore.maxKw).then(() => {
			let kwItems = agtLeadNowUiStore.currentKwOptionItems;
			let _empties = agtLeadNowUiStore.currentEmpties;
			kwItems = agenturListUiStore.applyFilterOnAgt(kwItems) as LeadNowVM[];
			_empties = agenturListUiStore.applyFilterOnAgt(_empties) as LeadNowVM[];
			setItems(kwItems);
			setEmpties(_empties);
		});
	}, [
		berichteUiiStore.selectedAgt,
		agenturListUiStore.filterHasChanged,
		agenturListUiStore,
		agtLeadNowUiStore.items,
		agtLeadNowUiStore.currentKwOptionItems,
		agtLeadNowUiStore.currentEmpties,
		agtLeadNowUiStore.maxKw,
		agtLeadNowUiStore.minKw,
		agtLeadNowUiStore,
	]);

	const onAboChange = (_name: string, obj: ISelectOpt) => {
		runInAction(() => {
			setAbo(obj);
			agtLeadNowUiStore.currentOption = obj;
		});
	};

	const toggleShowEmpties = () => {
		setShowEmpties(!showEmpties);
	};

	const handleSetInfoWindowData = (data: { anchor: google.maps.marker.AdvancedMarkerElement; item: LeadNowVM }) => {
		if (!data.item) {
			// zoom map on the marker
			const map = data.anchor.map;
			if (map) {
				const position = data.anchor.position;
				const mapZoom = map.getZoom() || 6;
				if (position) {
					map.setZoom(mapZoom + 2);
					map.panTo(position);
				}
			}
		}
		setInfoWindowData(data);
	};

	return (
		<>
			<div className="grid is-col-auto pad-1rem">
				<SelectInput
					label="Abo Typ"
					name="abos"
					emptyText="abos"
					value={abo.value}
					onChange={onAboChange}
					opts={LeadNowAbosOpts.concat(LeadNowNettoOpt)}
					isClearable={false}
					modifier="is-inline"
				/>
				<div>
					<Button className="button is-primary is-inverted" onClick={toggleShowEmpties}>
						<Icon iconClass={showEmpties ? 'check-square' : 'square'} />
						Kein AMIS.NOW Leads
					</Button>
				</div>
			</div>

			<APIProvider apiKey="AIzaSyAS6Sik2T9tlF-aWgWWg44Lkup6TiwDOI4">
				<Map
					mapId={'map'}
					mapTypeId="roadmap"
					defaultZoom={6}
					defaultCenter={{ lat: 51.1657, lng: 10.4515 }} // Center of Germany
					disableDefaultUI={true}
				>
					{showEmpties &&
						empties &&
						empties.map((item) => <ReusableSimpleMarker key={item.agt.agtId} position={{ lat: item.agt.lat!, lng: item.agt.lng! }} title={item.agt.name} color={colors.red} />)}
					{items && <ClusteredMarkers items={items} setNumClusters={() => {}} setInfowindowData={handleSetInfoWindowData} />}

					{infoWindowData && <LeadNowInfoWindowWrapper anchor={infoWindowData.anchor} onClose={() => setInfoWindowData(null)} />}
				</Map>
			</APIProvider>
		</>
	);
});

interface IClusteredMarkers {
	items: LeadNowVM[];
	setNumClusters: (num: number) => void;
	setInfowindowData: (data: { anchor: google.maps.marker.AdvancedMarkerElement; item: LeadNowVM }) => void;
}
export const ClusteredMarkers = (props: IClusteredMarkers) => {
	const { items, setNumClusters, setInfowindowData } = props;
	const agts = items.map((item) => item.agt);
	const { clusters } = useSupercluster(agts);

	useEffect(() => {
		setNumClusters(clusters.length);
	}, [setNumClusters, clusters.length]);

	const handleClusterClick = useCallback((marker: google.maps.marker.AdvancedMarkerElement, _clusterId: number) => {
		// zoom into map
		const map = marker.map;
		if (map) {
			const position = marker.position;
			const mapZoom = map.getZoom() || 6;
			if (position) {
				map.setZoom(mapZoom + 2);
				map.panTo(position);
			}
		}
	}, []);

	const handleMarkerClick = useCallback(
		(marker: google.maps.marker.AdvancedMarkerElement, featureId: string) => {
			const feature = clusters.find((feat) => feat.id === featureId) as Feature<Point>;
			const agtLeadNow = items.find((item) => item.agt.agtId === feature.id);
			if (!agtLeadNow) return;

			setInfowindowData({ anchor: marker, item: agtLeadNow });
		},
		[clusters, setInfowindowData],
	);

	return (
		<>
			{clusters.map((feature) => {
				const [lng, lat] = feature.geometry.coordinates;

				const clusterProperties = feature.properties as ClusterProperties;
				const isCluster: boolean = clusterProperties.cluster;

				return isCluster ? (
					<ReusableClusterFeatureMarker
						key={feature.id}
						clusterId={clusterProperties.cluster_id}
						position={{ lat, lng }}
						size={clusterProperties.point_count}
						onMarkerClick={handleClusterClick}
					/>
				) : (
					<ReusableFeatureMarker key={feature.id} featureId={feature.id as string} position={{ lat, lng }} onMarkerClick={handleMarkerClick} />
				);
			})}
		</>
	);
};

interface IFeaturesClusterMarker {
	clusterId: number;
	onMarkerClick?: (marker: google.maps.marker.AdvancedMarkerElement, clusterId: number) => void;
	position: google.maps.LatLngLiteral;
	size: number;
}
export const FeaturesClusterMarker = (props: IFeaturesClusterMarker) => {
	const { clusterId, onMarkerClick, position, size } = props;
	const [markerRef, marker] = useAdvancedMarkerRef();
	const handleClick = useCallback(() => onMarkerClick && onMarkerClick(marker!, clusterId), [onMarkerClick, marker, clusterId]);
	const markerSize = Math.floor(80 + Math.sqrt(size) * 2);

	return (
		<AdvancedMarker
			ref={markerRef}
			position={position}
			zIndex={size}
			onClick={handleClick}
			className={'marker cluster'}
			style={{ width: markerSize, height: markerSize }}
			anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
		>
			<MarkerSVG color={colors.primary} size={markerSize} text={size.toString()} />
		</AdvancedMarker>
	);
};

interface IFeatureMarker {
	position: google.maps.LatLngLiteral;
	featureId: string;
	onMarkerClick?: (marker: google.maps.marker.AdvancedMarkerElement, featureId: string) => void;
}
export const FeatureMarker = (props: IFeatureMarker) => {
	const { position, featureId, onMarkerClick } = props;
	const [markerRef, marker] = useAdvancedMarkerRef();
	const handleClick = useCallback(() => onMarkerClick && onMarkerClick(marker!, featureId), [onMarkerClick, marker, featureId]);

	return (
		<AdvancedMarker ref={markerRef} position={position} onClick={handleClick} anchorPoint={AdvancedMarkerAnchorPoint.CENTER} className={'marker feature'}>
			<AgtMarkerSVG />
		</AdvancedMarker>
	);
};

interface IMarkerSVG {
	color?: string;
	size?: number;
	text?: string;
}
export const MarkerSVG = (props: IMarkerSVG) => {
	const color = props.color || colors.primary;
	const size = props.size || 100; // Width of the SVG
	const r = size / 3; // make smaller so we don't extend the bounds of the SVG when animating
	const num = 3;

	return (
		<svg className="marker-svg" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
			{Array.from({ length: num }).map((_, i) => {
				return (
					<circle key={i} className={`c-${i + 1}`} cx={size / 2} cy={size / 2} r={r} fill={color} fillOpacity={0}>
						<animate attributeName="r" begin={`${i}s`} dur="3s" to={size / 2} repeatCount="indefinite" />
						<animate attributeName="fill-opacity" begin={`${i}s`} dur="3s" values="0;0.5;0" repeatCount="indefinite" />
					</circle>
				);
			})}
			{props.text && (
				<>
					<defs>
						<filter id="text-filter">
							<feDropShadow dx="0" dy="0" stdDeviation="1" floodColor={colors.primary} floodOpacity="1" />
							<feDropShadow dx="0" dy="0" stdDeviation="2" floodColor={colors.primary} floodOpacity="1" />
						</filter>
					</defs>
					<text filter="url(#text-filter)" x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize={12} fill={colors.white}>
						{props.text}
					</text>
				</>
			)}
		</svg>
	);
};

export const AgtMarkerSVG = (props: IMarkerSVG) => {
	const color = props.color || colors.primary;

	return (
		<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
			<circle cx="20" cy="20" r="20" fill={color} fillOpacity={0.7} />
			<path
				d="M16.5714 16.2031V14.7969C16.5714 14.5859 16.75 14.375 17 14.375H18.4286C18.6429 14.375 18.8571 14.5859 18.8571 14.7969V16.2031C18.8571 16.4492 18.6429 16.625 18.4286 16.625H17C16.75 16.625 16.5714 16.4492 16.5714 16.2031ZM21.5714 16.625C21.3214 16.625 21.1429 16.4492 21.1429 16.2031V14.7969C21.1429 14.5859 21.3214 14.375 21.5714 14.375H23C23.2143 14.375 23.4286 14.5859 23.4286 14.7969V16.2031C23.4286 16.4492 23.2143 16.625 23 16.625H21.5714ZM17 20C16.75 20 16.5714 19.8242 16.5714 19.5781V18.1719C16.5714 17.9609 16.75 17.75 17 17.75H18.4286C18.6429 17.75 18.8571 17.9609 18.8571 18.1719V19.5781C18.8571 19.8242 18.6429 20 18.4286 20H17ZM21.5714 20C21.3214 20 21.1429 19.8242 21.1429 19.5781V18.1719C21.1429 17.9609 21.3214 17.75 21.5714 17.75H23C23.2143 17.75 23.4286 17.9609 23.4286 18.1719V19.5781C23.4286 19.8242 23.2143 20 23 20H21.5714ZM18.8571 22.9531C18.8571 23.1992 18.6429 23.375 18.4286 23.375H17C16.75 23.375 16.5714 23.1992 16.5714 22.9531V21.5469C16.5714 21.3359 16.75 21.125 17 21.125H18.4286C18.6429 21.125 18.8571 21.3359 18.8571 21.5469V22.9531ZM21.5714 23.375C21.3214 23.375 21.1429 23.1992 21.1429 22.9531V21.5469C21.1429 21.3359 21.3214 21.125 21.5714 21.125H23C23.2143 21.125 23.4286 21.3359 23.4286 21.5469V22.9531C23.4286 23.1992 23.2143 23.375 23 23.375H21.5714ZM28 27.7344V29H12V27.7344C12 27.5234 12.1786 27.3125 12.4286 27.3125H13.1071V11.8438C13.1071 11.3867 13.5 11 13.9643 11H26C26.4643 11 26.8571 11.3867 26.8571 11.8438V27.3125H27.5714C27.7857 27.3125 28 27.5234 28 27.7344ZM14.8214 27.2773H18.8571V24.9219C18.8571 24.7109 19.0357 24.5 19.2857 24.5H20.7143C20.9286 24.5 21.1429 24.7109 21.1429 24.9219V27.2773H25.1429V12.7227L14.8571 12.6875L14.8214 27.2773Z"
				fill="white"
			/>
		</svg>
	);
};

interface IMarkerWithInfowindowProps {
	item: LeadNowVM;
	color?: string;
}
export const SimpleMarker = (props: IMarkerWithInfowindowProps) => {
	const { item, color } = props;
	const agt = item.agt;

	if (!agt || !agt.lat || !agt.lng) {
		return null; // Return null if the agent does not have valid coordinates
	}
	return (
		<AdvancedMarker position={{ lat: agt.lat, lng: agt.lng }} title={item.agt.name}>
			<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
				<circle cx="10" cy="10" r="10" fill={color} fillOpacity={0.3} />
			</svg>
		</AdvancedMarker>
	);
};

interface ILeadNowInfoWindowWrapper {
	anchor: google.maps.marker.AdvancedMarkerElement;
	onClose: () => void;
}

export const LeadNowInfoWindowWrapper = (props: ILeadNowInfoWindowWrapper) => {
	const { anchor, onClose } = props;
	const { agtLeadNowUiStore } = useStore();
	let kwItems = agtLeadNowUiStore.currentKwOptionItems;

	const lat = typeof anchor.position?.lat === 'function' ? anchor.position.lat() : anchor.position?.lat;
	const lng = typeof anchor.position?.lng === 'function' ? anchor.position.lng() : anchor.position?.lng;

	const matchedAgts = kwItems.filter((i) => i.agt.lat === lat && i.agt.lng === lng);

	const leadNowItems = matchedAgts.map((item: LeadNowVM) => ({
		agt: { name: item.agt.name, agtId: item.agt.agtId },
		netto: item.netto,
		brutto: item.brutto,
	}));

	if (!lat || !lng) {
		return null;
	}

	return <LeadNowInfoWindow position={{ lat, lng }} onClose={onClose} items={leadNowItems} />;
};
